import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { toast } from 'react-hot-toast';

import {
    Button,
    Card,
    CardTitle,
    Spinner
} from '@/components/ui';
import {
    Input,
    Select,
    ProgressIndicator
} from '@/components/forms';
import { PageHeader } from '@/components/layout';
import { ROUTES } from '@/constants/routes';
import { useEquipmentList } from '@/api/useEquipment';
import { useCreateTechnicalAssessment, useTechnicalAssessmentDetail, useUpdateTechnicalAssessment } from '@/api/useTechnicalAssessments';
import type { TechnicalAssessmentData } from '@/types/mmms';
import { useAuthStore } from '@/stores/useAuthStore';
import { getCurrentWeekRange } from '@/utils/dateUtils';

import styles from './TechnicalAssessment.module.css';

const STEPS = [
    { label: 'Información General' },
    { label: 'Evaluación de Equipos' },
    { label: 'Resumen y Envío' }
];

const CONDITION_OPTIONS = [
    { value: 'excellent', label: 'Excelente' },
    { value: 'good', label: 'Bueno' },
    { value: 'fair', label: 'Regular' },
    { value: 'critical', label: 'Crítico' }
];

const SYSTEM_ISSUES: Record<string, string[]> = {
    'HIDRÁULICO': ['Fugas en mangueras', 'Bajo nivel de aceite', 'Ruido en bomba', 'Lentitud en cilindros'],
    'MOTOR': ['Humo excesivo', 'Falla de encendido', 'Calentamiento', 'Baja presión de aceite'],
    'ELÉCTRICO': ['Fallas de luces', 'Batería baja', 'Problemas de sensores', 'Cortocircuito'],
    'TRANSMISIÓN': ['Cambios bruscos', 'Patina embrague', 'Ruido en caja', 'Fuga en convertidor'],
    'FRENOS': ['Baja presión', 'Discos desgastados', 'Ruido al frenar', 'Fuga de líquido/aire'],
    'ESTRUCTURA': ['Grietas en chasis', 'Pernos flojos', 'Corrosión excesiva', 'Desgaste en articulaciones']
};



export function TechnicalAssessment() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;
    const { getUser } = useAuthStore();
    const currentUser = getUser();
    const currentWeek = getCurrentWeekRange();

    const [currentStep, setCurrentStep] = useState(0);

    const { data: equipmentList, isLoading: loadingEquipment } = useEquipmentList();
    const { data: assessmentDetail, isLoading: loadingDetail } = useTechnicalAssessmentDetail(id || '');
    const { mutate: createAssessment, isPending: creating } = useCreateTechnicalAssessment();
    const { mutate: updateAssessment, isPending: updating } = useUpdateTechnicalAssessment();

    const isSubmitting = creating || updating;

    const { control, handleSubmit, reset, trigger } = useForm<TechnicalAssessmentData>({
        defaultValues: {
            report_number: `AT-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`,
            date: new Date().toISOString().split('T')[0],
            title: '',
            technician_name: currentUser?.username || '',
            recipient_name: 'Residente de Mantenimiento',
            summary: '',
            recommendations: '',
            status: 'draft',
            equipment_evaluations: []
        }
    });

    const { fields: equipFields, append: appendEquip, remove: removeEquip } = useFieldArray({
        control,
        name: "equipment_evaluations"
    });

    useEffect(() => {
        if (isEdit && assessmentDetail) {
            reset(assessmentDetail);
        }
    }, [isEdit, assessmentDetail, reset]);

    const onSubmit = (data: TechnicalAssessmentData) => {
        const payload = {
            ...data,
            equipment_evaluations_write: data.equipment_evaluations,
            status: 'submitted' as const
        };
        if (isEdit) {
            updateAssessment({ id: id!, data: payload }, {
                onSuccess: () => {
                    toast.success('Evaluación técnica actualizada');
                    navigate(ROUTES.home);
                }
            });
        } else {
            createAssessment(payload, {
                onSuccess: () => {
                    toast.success('Evaluación técnica enviada');
                    navigate(ROUTES.home);
                }
            });
        }
    };

    const nextStep = async () => {
        let isValid = false;

        if (currentStep === 0) {
            isValid = await trigger(['date', 'title', 'technician_name']);
        } else if (currentStep === 1) {
            isValid = true;
            if (equipFields.length === 0) {
                toast.error("Debe agregar al menos un equipo para evaluar");
                isValid = false;
            } else {
                // Optionally validate internal fields of equipment if needed
                // isValid = await trigger('equipment_evaluations');
            }
        } else {
            isValid = true;
        }

        if (isValid) {
            setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
            window.scrollTo(0, 0);
        }
    };

    const prevStep = () => {
        setCurrentStep(prev => Math.max(prev - 1, 0));
        window.scrollTo(0, 0);
    };

    const addEquipment = () => {
        appendEquip({
            equipment_id: 0,
            condition: 'good',
            is_operative: true,
            total_estimated_cost: 0,
            priority: 'medium',
            findings: []
        });
    };

    if (loadingEquipment || (isEdit && loadingDetail)) {
        return <div className={styles.loading}><Spinner size="lg" /></div>;
    }

    return (
        <div className={styles.container}>
            <PageHeader
                title={isEdit ? "Editar Evaluación Técnica" : "Nueva Evaluación Técnica"}
                subtitle={`Evaluación detallada de estado de equipos • ${currentWeek.label}`}
                backPath={ROUTES.home}
            />

            <ProgressIndicator steps={STEPS} currentStep={currentStep} />

            <form onSubmit={handleSubmit(onSubmit)}>
                {currentStep === 0 && (
                    <Card className={styles.stepCard} variant="full">
                        <CardTitle variant="simple">Información General</CardTitle>
                        <div className={styles.formGrid}>
                            <Controller
                                name="report_number"
                                control={control}
                                render={({ field }) => (
                                    <Input name="report_number" label="N° Reporte" isReadOnly value={field.value} setValue={field.onChange} />
                                )}
                            />
                            <Controller
                                name="date"
                                control={control}
                                rules={{ required: "La fecha es requerida" }}
                                render={({ field, fieldState }) => (
                                    <Input
                                        name="date"
                                        label="Fecha"
                                        type="date"
                                        value={field.value}
                                        setValue={field.onChange}
                                        isInvalid={!!fieldState.error}
                                        errorMessage={fieldState.error?.message}
                                    />
                                )}
                            />
                        </div>
                        <Controller
                            name="title"
                            control={control}
                            rules={{ required: "El asunto/título es requerido" }}
                            render={({ field, fieldState }) => (
                                <Input
                                    name="title"
                                    label="Asunto / Título"
                                    placeholder="Ej: Evaluación de Jumbo Sandvik..."
                                    value={field.value}
                                    setValue={field.onChange}
                                    isRequired
                                    isInvalid={!!fieldState.error}
                                    errorMessage={fieldState.error?.message}
                                />
                            )}
                        />
                        <div className={styles.formGrid}>
                            <Controller
                                name="technician_name"
                                control={control}
                                rules={{ required: "El nombre del técnico es requerido" }}
                                render={({ field, fieldState }) => (
                                    <Input
                                        name="technician_name"
                                        label="Técnico Evaluador"
                                        placeholder="Nombre del técnico..."
                                        value={field.value || ''}
                                        setValue={field.onChange}
                                        isRequired
                                        isInvalid={!!fieldState.error}
                                        errorMessage={fieldState.error?.message}
                                    />
                                )}
                            />
                            <Controller
                                name="recipient_name"
                                control={control}
                                render={({ field }) => (
                                    <Input name="recipient_name" label="Destinatario" placeholder="Nombre del destinatario..." value={field.value || ''} setValue={field.onChange} />
                                )}
                            />
                        </div>
                    </Card>
                )}

                {currentStep === 1 && (
                    <div className={styles.equipList}>
                        {equipFields.map((equipField, equipIndex) => (
                            <EquipmentAssessmentCard
                                key={equipField.id}
                                equipIndex={equipIndex}
                                control={control}
                                equipmentList={equipmentList || []}
                                onRemove={() => removeEquip(equipIndex)}
                            />
                        ))}
                        <Button type="button" variant="secondary" onClick={addEquipment} className={styles.addBtn}>
                            <i className="fa-solid fa-plus"></i> Agregar Equipo a Evaluar
                        </Button>
                    </div>
                )}

                {currentStep === 2 && (
                    <Card className={styles.stepCard} variant="full">
                        <CardTitle variant="simple">Resumen y Comentarios Finales</CardTitle>
                        <Controller
                            name="summary"
                            control={control}
                            render={({ field }) => (
                                <Input name="summary" label="Resumen Ejecutivo" multiline rows={4} placeholder="Escriba un resumen del estado del equipo..." value={field.value || ''} setValue={field.onChange} />
                            )}
                        />
                        <Controller
                            name="recommendations"
                            control={control}
                            render={({ field }) => (
                                <Input name="recommendations" label="Acciones Sugeridas" multiline rows={3} placeholder="Mantenimiento preventivo, cambio de componentes, etc..." value={field.value || ''} setValue={field.onChange} />
                            )}
                        />
                        <div className={styles.verificationBox}>
                            <p className={styles.warningText}>
                                Esta evaluación técnica será enviada para revisión. Asegúrese de que todos los costos estimados sean precisos.
                            </p>
                        </div>
                    </Card>
                )}

                <div className={styles.actions}>
                    {currentStep > 0 && <Button type="button" variant="secondary" onClick={prevStep}>Anterior</Button>}
                    <span className={styles.spacer}></span>
                    {currentStep < STEPS.length - 1 ? (
                        <Button type="button" onClick={nextStep}>Continuar</Button>
                    ) : (
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? <Spinner variant="secondary" size="sm" /> : (isEdit ? 'Actualizar Evaluación' : 'Enviar Evaluación')}
                        </Button>
                    )}
                </div>
            </form>
        </div>
    );
}

function EquipmentAssessmentCard({ equipIndex, control, equipmentList, onRemove }: any) {
    const { fields: findingFields, append: appendFinding, remove: removeFinding } = useFieldArray({
        control,
        name: `equipment_evaluations.${equipIndex}.findings`
    });

    const [selectedSystem, setSelectedSystem] = useState<string>('');

    const addIssueAsFinding = (issue: string) => {
        appendFinding({
            system_name: selectedSystem,
            description: issue,
            status: 'fair',
            estimated_cost: 0
        });
    };

    return (
        <Card className={styles.equipCard} variant="full">
            <div className={styles.equipCardHeader}>
                <Controller
                    name={`equipment_evaluations.${equipIndex}.equipment_id`}
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Equipo"
                            value={field.value.toString()}
                            onChange={(e) => field.onChange(parseInt(e.target.value))}
                            options={equipmentList.map((e: any) => ({ value: e.id.toString(), label: e.code }))}
                        />
                    )}
                />
                <Button type="button" variant="danger" size="sm" onClick={onRemove} className={styles.removeBtn}>
                    <i className="fa-solid fa-trash"></i>
                </Button>
            </div>

            <div className={styles.formGrid}>
                <Controller
                    name={`equipment_evaluations.${equipIndex}.condition`}
                    control={control}
                    render={({ field }) => (
                        <Select label="Estado General" value={field.value} onChange={(e) => field.onChange(e.target.value)} options={CONDITION_OPTIONS} />
                    )}
                />
                <Controller
                    name={`equipment_evaluations.${equipIndex}.is_operative`}
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Operatividad"
                            value={field.value ? 'true' : 'false'}
                            onChange={(e) => field.onChange(e.target.value === 'true')}
                            options={[{ value: 'true', label: 'Operativo' }, { value: 'false', label: 'Inoperativo' }]}
                        />
                    )}
                />
            </div>

            <div className={styles.findingsSection}>
                <h4 className={styles.findingsTitle}>Evaluación por Sistemas Críticos</h4>

                <div className={styles.systemSelector}>
                    <Select
                        label="Seleccionar Sistema para Evaluar"
                        value={selectedSystem}
                        onChange={(e) => setSelectedSystem(e.target.value)}
                        options={Object.keys(SYSTEM_ISSUES).map(sys => ({ value: sys, label: sys }))}
                        placeholder="Elija un sistema..."
                    />

                    {selectedSystem && (
                        <div className={`${styles.issuesGrid} fade-in`}>
                            <p className={styles.issuesLabel}>Problemas comunes en {selectedSystem}:</p>
                            <div className={styles.issuesList}>
                                {SYSTEM_ISSUES[selectedSystem].map(issue => (
                                    <button
                                        key={issue}
                                        type="button"
                                        className={styles.issueBtn}
                                        onClick={() => addIssueAsFinding(issue)}
                                    >
                                        + {issue}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.findingsList}>
                    {findingFields.length > 0 && <h5 className={styles.findingsSubTitle}>Hallazgos Registrados</h5>}
                    {findingFields.map((finding, fIndex) => (
                        <div key={finding.id} className={styles.findingRow}>
                            <div className={styles.findingMain}>
                                <Controller
                                    name={`equipment_evaluations.${equipIndex}.findings.${fIndex}.system_name`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input name={`sys-${equipIndex}-${fIndex}`} label="Sistema" value={field.value} setValue={field.onChange} size="sm" placeholder="Ej: Hidráulico" />
                                    )}
                                />
                                <Controller
                                    name={`equipment_evaluations.${equipIndex}.findings.${fIndex}.description`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input name={`desc-${equipIndex}-${fIndex}`} label="Descripción / Falla" value={field.value || ''} setValue={field.onChange} size="sm" placeholder="Ej: Falla en el motor" />
                                    )}
                                />
                            </div>
                            <div className={styles.findingDetails}>
                                <Controller
                                    name={`equipment_evaluations.${equipIndex}.findings.${fIndex}.status`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select label="Estado" value={field.value} onChange={(e) => field.onChange(e.target.value)} options={CONDITION_OPTIONS.slice(1)} size="sm" />
                                    )}
                                />
                                <Controller
                                    name={`equipment_evaluations.${equipIndex}.findings.${fIndex}.estimated_cost`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            name={`cost-${equipIndex}-${fIndex}`}
                                            label="Costo Est ($)"
                                            type="number"
                                            value={field.value.toString()}
                                            setValue={(v) => field.onChange(parseFloat(v) || 0)}
                                            size="sm"
                                            placeholder="Ej: 1000"
                                        />
                                    )}
                                />
                                <Button type="button" variant="secondary" size="sm" onClick={() => removeFinding(fIndex)} className={styles.removeFindingBtn}>×</Button>
                            </div>
                        </div>
                    ))}
                </div>

                <Button type="button" variant="secondary" size="sm" onClick={() => appendFinding({ system_name: '', status: 'good', priority: 'medium', estimated_cost: 0 })} className={styles.addFindingBtn}>
                    + Agregar Hallazgo Manual
                </Button>
            </div>
        </Card>
    );
}
